/* @flow */
import idx from 'idx';
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import { withRouter } from 'app/context/Router';

import AdminUpgradeInformationalPage from 'admin/components/AdminUpgradeInformationalPage';
import AdminUpgradeSharedQuoteFound from 'admin/components/AdminUpgradeSharedQuoteFound';
import Button from 'components/Button';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import EventTracking from 'lib/EventTracking';
import Link from 'components/Link';

import SHARED_QUOTE_QUERY from 'admin/queries/AdminUpgradeSharedQuoteQuery.gql';

import type { AdminUpgradeSharedQuote } from 'admin/queries/__generated__/AdminUpgradeSharedQuote';

import './styles/AdminUpgradePageView.scss';

type Props = {
  router: *,
  shareToken: ?string,
  data: ReactApolloData<AdminUpgradeSharedQuote>,
};

class AdminUpgradeSharedQuotePage extends React.Component<Props> {
  componentDidMount() {
    const { data } = this.props;
    const quote = data.sharedQuote;
    const quoteState = idx(data.sharedQuote, _ => _.quoteState);

    if (quote && quoteState) {
      this.trackViewEvents(quoteState);
    }
  }

  componentDidUpdate(prevProps: Object) {
    const { data } = this.props;
    const quote = data.sharedQuote;
    const quoteState = idx(data.sharedQuote, _ => _.quoteState);
    const prevQuote = prevProps.data.sharedQuote;

    if (quote && quoteState && !prevQuote) {
      this.trackViewEvents(quoteState);
    }
  }

  track = (prefix: string, key: string) => {
    EventTracking.trackKey(`${prefix}.${key}`);
  };

  trackViewEvents = (quoteState: string) => {
    if (quoteState === 'expired') {
      this.track('admin_self_serve.shared_quote_expired', 'view');
    } else if (quoteState === 'already_sent') {
      this.track('admin_self_serve.shared_quote_completed', 'view');
    } else if (quoteState !== 'found') {
      // tracking for 'found' is in AdminUpgradeSharedQuoteFound
      this.track('admin_self_serve.shared_quote_not_found', 'view');
    }
  };

  render() {
    const { data } = this.props;
    const { loading } = data;
    const quote = idx(data.sharedQuote, _ => _.quoteData);
    const quoteState = idx(data.sharedQuote, _ => _.quoteState);
    const { from_sales: fromSales } = this.props.router.location.query;

    if (loading) {
      return <CenteredLoadingIndicator data-test="loading" />;
    } else {
      if (quote && quoteState === 'found') {
        return (
          <AdminUpgradeSharedQuoteFound
            quote={quote}
            referredFromSales={(fromSales || '') === 'true'}
          />
        );
      } else if (quoteState === 'expired') {
        this.track('admin_self_serve.shared_quote_expired', 'view');
        return (
          <AdminUpgradeInformationalPage
            title="The quote you're looking for has expired"
            subtext="Remind School and District Plan quotes expire after 60 days."
            action={
              <Button
                to="/admin_upgrade/"
                onClick={() =>
                  this.track('admin_self_serve.shared_quote_expired', 'continue.click')
                }>
                Get a new quote
              </Button>
            }
          />
        );
      } else if (quoteState === 'already_sent') {
        return (
          <AdminUpgradeInformationalPage
            title="This purchase has already been completed"
            subtext={
              <span>
                Have questions about what’s next? Email our School Success team for help at{' '}
                <Link href="mailto:schoolplan@remindhq.com">schoolplan@remindhq.com</Link>.
              </span>
            }
            action={
              <Button
                onClick={() =>
                  this.track('admin_self_serve.shared_quote_completed', 'continue.click')
                }
                to="/">
                Go to Remind
              </Button>
            }
          />
        );
      }
      return (
        <AdminUpgradeInformationalPage
          title="We couldn't find the quote you're looking for"
          subtext="It'll only take a minute to get a quote for your school or district."
          action={
            <Button
              onClick={() =>
                this.track('admin_self_serve.shared_quote_not_found', 'continue.click')
              }
              to="/admin_upgrade/">
              Get a quote
            </Button>
          }
        />
      );
    }
  }
}

const withData = graphql(SHARED_QUOTE_QUERY, {
  skip: ({ shareToken }) => !shareToken,
  options: ({ shareToken }) => ({
    variables: {
      shareToken,
    },
    fetchPolicy: 'network-only',
  }),
});

export { AdminUpgradeSharedQuotePage };
export default compose(
  withRouter,
  withData
)(AdminUpgradeSharedQuotePage);
